import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { errorMessage } from '../../lib/api';
import { NAV_BADGES_QUERY_KEY, type NavBadges } from '../../lib/nav-badges';
import { trpc } from '../../lib/trpc';
import { toast } from '../../ui/Toast';

/** Po zmianie stanu uwagi: lista, podgląd, liczniki Inboxu, plakietki menu i kafelki Dziś (alarmy). */
export function useInboxRefresh() {
  const utils = trpc.useUtils();
  const queryClient = useQueryClient();
  return useCallback(
    () =>
      Promise.all([
        utils.inbox.invalidate(),
        utils.today.invalidate(),
        queryClient.invalidateQueries({ queryKey: NAV_BADGES_QUERY_KEY }),
      ]),
    [utils, queryClient],
  );
}

/** Mutacje stanu uwagi z odświeżeniem i komunikatem błędu. */
export function useInboxActions() {
  const refresh = useInboxRefresh();
  const onError = (err: unknown) => toast.error(errorMessage(err, 'Nie udało się zapisać zmiany.'));
  const opts = { onSuccess: () => void refresh(), onError };
  return {
    markRead: trpc.inbox.markRead.useMutation(opts),
    markUnread: trpc.inbox.markUnread.useMutation(opts),
    markDone: trpc.inbox.markDone.useMutation(opts),
    restore: trpc.inbox.restore.useMutation(opts),
    snooze: trpc.inbox.snooze.useMutation(opts),
    markAllRead: trpc.inbox.markAllRead.useMutation(opts),
  };
}

/** Liczniki Inboxu (plakietka „Inbox”, link „Odłożone”). */
export function useInboxCounts(enabled = true) {
  return trpc.inbox.counts.useQuery(undefined, { enabled, staleTime: 30_000, refetchInterval: 60_000 });
}

/**
 * Plakietka menu dla Inboxu (DESIGN §5: „Unread” — etykieta i liczba pogrubione). Do złożenia w źródle plakietek
 * (`setNavBadgesSource`, kompozycja Fali 3) razem z Akceptacjami:
 *   setNavBadgesSource(async () => ({ ...(await inboxNavBadges(() => trpcClient.inbox.counts.query())), ... }))
 */
export async function inboxNavBadges(fetchCounts: () => Promise<{ unread: number }>): Promise<NavBadges> {
  const counts = await fetchCounts();
  return counts.unread > 0 ? { inbox: { kind: 'unread', count: counts.unread } } : {};
}
